import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertCircle, Loader, Bell, BellOff } from 'lucide-react';
import { ProcessingStage } from '../types';

interface ProcessingStats {
  eventsFound?: number;
  citationsFound?: number;
  pagesProcessed?: number;
  totalPages?: number;
}

interface ProcessingProgressProps {
  stages: ProcessingStage[];
  stats?: ProcessingStats;
  elapsedTime?: number;
  documentName?: string;
  onNotifyMe?: () => void;
  notificationEnabled?: boolean;
  showNotifyButton?: boolean;
}

export const ProcessingProgress: React.FC<ProcessingProgressProps> = ({ 
  stages,
  stats,
  elapsedTime = 0,
  documentName,
  onNotifyMe,
  notificationEnabled = false,
  showNotifyButton = false
}) => {
  const [displayTime, setDisplayTime] = useState(elapsedTime);

  useEffect(() => {
    setDisplayTime(elapsedTime);
  }, [elapsedTime]);

  const getStageIcon = (status: ProcessingStage['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Loader className="h-5 w-5 text-cyan-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-medical-400" />;
    }
  };

  const getStageColor = (status: ProcessingStage['status']) => {
    switch (status) {
      case 'completed':
        return 'text-green-700';
      case 'in_progress':
        return 'text-cyan-700 font-medium';
      case 'failed':
        return 'text-red-700';
      default:
        return 'text-medical-500';
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeMessage = (): string => {
    if (displayTime < 30) return 'Usually completes in 1-2 minutes';
    if (displayTime < 90) return 'Almost done...';
    if (displayTime < 150) return 'Taking longer than usual...';
    return 'Still working...';
  };

  const getOverallProgress = (): number => {
    const completedStages = stages.filter(s => s.status === 'completed').length;
    const totalStages = stages.length;
    if (totalStages === 0) return 0;
    
    // If there's a stage in progress with its own progress, factor that in
    const inProgressStage = stages.find(s => s.status === 'in_progress');
    if (inProgressStage && inProgressStage.progress !== undefined) {
      const stageWeight = 1 / totalStages;
      const inProgressContribution = (inProgressStage.progress / 100) * stageWeight;
      return Math.round(((completedStages / totalStages) + inProgressContribution) * 100);
    }
    
    return Math.round((completedStages / totalStages) * 100);
  };

  const currentStage = stages.find(s => s.status === 'in_progress');
  const isLongWait = displayTime > 60;

  return (
    <div className="bg-white rounded-lg border border-medical-200 p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-medical-900">
          Processing Document
        </h3>
        <div className="text-sm text-medical-600 flex items-center space-x-2">
          <Clock className="h-4 w-4" />
          <span>{formatTime(displayTime)}</span>
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-medical-600">{getTimeMessage()}</span>
          <span className="text-medical-700 font-medium">{getOverallProgress()}%</span>
        </div>
        <div className="bg-medical-200 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${getOverallProgress()}%` }}
          />
        </div>
      </div>

      {/* Document Name */}
      {documentName && (
        <div className="text-sm text-medical-600">
          Processing {documentName}
        </div>
      )}

      {/* Stages */}
      <div className="space-y-3">
        {stages.map((stage, index) => (
          <div key={index} className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">
              {getStageIcon(stage.status)}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm ${getStageColor(stage.status)}`}>
                Stage {index + 1}: {stage.stage}
                {stage.duration && stage.status === 'completed' && (
                  <span className="text-medical-500 ml-2">
                    ({formatTime(stage.duration)})
                  </span>
                )}
              </div>
              {stage.status === 'in_progress' && stage.message && (
                <div className="text-xs text-medical-600 mt-1">
                  └─ {stage.message}
                </div>
              )}
              {stage.status === 'in_progress' && stage.progress !== undefined && (
                <div className="mt-2">
                  <div className="bg-medical-200 rounded-full h-1.5">
                    <div 
                      className="bg-cyan-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${stage.progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      {stats && (stats.eventsFound !== undefined || stats.citationsFound !== undefined) && (
        <div className="flex items-center space-x-4 text-sm text-medical-600 pt-2 border-t border-medical-200">
          {stats.eventsFound !== undefined && (
            <div>
              ✓ Found {stats.eventsFound} medical event{stats.eventsFound !== 1 ? 's' : ''}
            </div>
          )}
          {stats.citationsFound !== undefined && (
            <div>
              ✓ Found {stats.citationsFound} citation{stats.citationsFound !== 1 ? 's' : ''}
            </div>
          )}
          {stats.pagesProcessed !== undefined && stats.totalPages !== undefined && (
            <div>
              Processing page {stats.pagesProcessed} of {stats.totalPages}
            </div>
          )}
        </div>
      )}

      {/* Long Wait Message */}
      {isLongWait && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-md p-4 space-y-3">
          <div className="text-sm text-cyan-900">
            {displayTime > 150 ? (
              <>
                <strong>This is taking longer than usual ({formatTime(displayTime)})</strong>
                <p className="mt-1">
                  You can safely navigate away. We'll notify you when processing is complete.
                </p>
              </>
            ) : (
              <>
                Processing large documents can take 1-2 minutes. You can navigate away and we'll notify you when it's ready.
              </>
            )}
          </div>
          
          {showNotifyButton && onNotifyMe && (
            <button
              onClick={onNotifyMe}
              disabled={notificationEnabled}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                notificationEnabled
                  ? 'bg-green-100 text-green-700 cursor-default'
                  : 'bg-cyan-600 text-white hover:bg-cyan-700'
              }`}
            >
              {notificationEnabled ? (
                <>
                  <Bell className="h-4 w-4" />
                  <span>✓ Notifications Enabled</span>
                </>
              ) : (
                <>
                  <BellOff className="h-4 w-4" />
                  <span>Notify Me When Complete</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Tip */}
      {!isLongWait && (
        <div className="text-xs text-medical-500 flex items-center space-x-1">
          <span>💡</span>
          <span>Tip: Large documents may take 1-2 minutes to process</span>
        </div>
      )}
    </div>
  );
};